'use client';

import Script from 'next/script';
import * as Sentry from '@sentry/nextjs';
import { createContext, useContext, useEffect, useState } from 'react';
import { useLocale } from 'next-intl';
import {
  createPaddleCheckout,
  createSalesAgentPaddleCheckout,
  getPaddleSubscription,
  PaddleRefundConsent,
  PaddleSubscriptionStatus,
  PaddleSubscriptionUpgradePreview,
  previewPaddleSubscriptionUpgrade,
  revokePaddleSubscriptionDowngrade,
  schedulePaddleSubscriptionDowngrade,
  upgradePaddleSubscription,
} from '@/entities/billing/api/paddle.service';
import { PaddleCheckoutPlanKey } from '@/entities/billing/lib/paddlePlans';
import { isPaddleEnabled } from '@/entities/billing/lib/paddleFeature';
import { routing } from '@/i18n/routing';
import { useAuth } from '@/shared/providers/AuthProvider';

type PaddleCheckout = {
  open: (options: {
    transactionId: string;
    settings: { successUrl: string };
  }) => void;
};

declare global {
  interface Window {
    Paddle?: {
      Environment: { set: (environment: 'sandbox') => void };
      Initialize: (options: { token: string }) => void;
      Checkout: PaddleCheckout;
    };
    __wingingPaddleInitialized?: boolean;
  }
}

type PaddleCheckoutContextValue = {
  isReady: boolean;
  openCheckout: (
    plan: PaddleCheckoutPlanKey,
    consent?: PaddleRefundConsent
  ) => Promise<void>;
  openSalesAgentCheckout?: (consent: PaddleRefundConsent) => Promise<void>;
  previewUpgrade: (
    plan: PaddleCheckoutPlanKey
  ) => Promise<PaddleSubscriptionUpgradePreview>;
  upgradePlan: (
    plan: PaddleCheckoutPlanKey,
    consent?: PaddleRefundConsent
  ) => Promise<void>;
  scheduleDowngrade: (plan: PaddleCheckoutPlanKey) => Promise<void>;
  revokeDowngrade: () => Promise<void>;
  subscription: PaddleSubscriptionStatus | null | undefined;
  subscriptionError: boolean;
};

const PaddleCheckoutContext = createContext<PaddleCheckoutContextValue | null>(
  null
);

export const PaddleCheckoutProvider = ({
  children,
  salesAgentReturnTo,
}: {
  children: React.ReactNode;
  salesAgentReturnTo?: string;
}) => {
  const [isReady, setIsReady] = useState(false);
  const [subscription, setSubscription] =
    useState<PaddleSubscriptionStatus | null>();
  const [subscriptionError, setSubscriptionError] = useState(false);
  const { user } = useAuth();
  const locale = useLocale();
  const token = process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN;
  const environment = process.env.NEXT_PUBLIC_PADDLE_ENVIRONMENT;
  const enabled = isPaddleEnabled();

  useEffect(() => {
    let isMounted = true;

    if (!enabled || !user) {
      setSubscription(null);
      setSubscriptionError(false);
      return;
    }

    setSubscription(undefined);
    setSubscriptionError(false);
    getPaddleSubscription()
      .then((currentSubscription) => {
        if (isMounted) {
          setSubscription(currentSubscription);
          setSubscriptionError(false);
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        if (isMounted) {
          setSubscriptionError(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [enabled, user]);

  const initialize = () => {
    if (!enabled || !window.Paddle || !token) return;

    try {
      if (!window.__wingingPaddleInitialized) {
        if (environment === 'sandbox') {
          window.Paddle.Environment.set('sandbox');
        }
        window.Paddle.Initialize({ token });
        window.__wingingPaddleInitialized = true;
      }
      setIsReady(true);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const openCheckout = async (
    plan: PaddleCheckoutPlanKey,
    consent?: PaddleRefundConsent
  ) => {
    if (!enabled || !window.Paddle || !isReady) return;

    const { transactionId } = await createPaddleCheckout(plan, consent);
    const localePrefix = locale === routing.defaultLocale ? '' : `/${locale}`;
    window.Paddle.Checkout.open({
      transactionId,
      settings: {
        successUrl: `${window.location.origin}${localePrefix}/dashboard/billing/paddle/success`,
      },
    });
  };

  const openSalesAgentCheckout = async (consent: PaddleRefundConsent) => {
    if (!enabled || !window.Paddle || !isReady) return;

    const { transactionId } = await createSalesAgentPaddleCheckout(consent);
    const localePrefix = locale === routing.defaultLocale ? '' : `/${locale}`;
    window.Paddle.Checkout.open({
      transactionId,
      settings: {
        successUrl: `${window.location.origin}${localePrefix}/sales-studio/billing?checkout=success${
          salesAgentReturnTo
            ? `&returnTo=${encodeURIComponent(salesAgentReturnTo)}`
            : ''
        }`,
      },
    });
  };

  const previewUpgrade = async (plan: PaddleCheckoutPlanKey) =>
    previewPaddleSubscriptionUpgrade(plan);

  const upgradePlan = async (
    plan: PaddleCheckoutPlanKey,
    consent?: PaddleRefundConsent
  ) => {
    await upgradePaddleSubscription(plan, consent);
    setSubscription((current) =>
      current
        ? {
            ...current,
            status: 'active',
            tier: plan === 'standard' ? 'pro' : 'starter',
          }
        : current
    );
  };

  const scheduleDowngrade = async (plan: PaddleCheckoutPlanKey) => {
    const { effectiveAt } = await schedulePaddleSubscriptionDowngrade(plan);
    setSubscription((current) =>
      current
        ? {
            ...current,
            pendingTier: 'starter',
            pendingTierEffectiveAt: effectiveAt,
          }
        : current
    );
  };

  const revokeDowngrade = async () => {
    await revokePaddleSubscriptionDowngrade();
    setSubscription((current) =>
      current
        ? {
            ...current,
            pendingTier: undefined,
            pendingTierEffectiveAt: undefined,
          }
        : current
    );
  };

  return (
    <PaddleCheckoutContext.Provider
      value={{
        isReady,
        openCheckout,
        openSalesAgentCheckout,
        previewUpgrade,
        upgradePlan,
        scheduleDowngrade,
        revokeDowngrade,
        subscription,
        subscriptionError,
      }}
    >
      {enabled && token && (
        <Script
          src="https://cdn.paddle.com/paddle/v2/paddle.js"
          strategy="afterInteractive"
          onLoad={initialize}
          onReady={initialize}
        />
      )}
      {children}
    </PaddleCheckoutContext.Provider>
  );
};

export const usePaddleCheckout = (): PaddleCheckoutContextValue => {
  const context = useContext(PaddleCheckoutContext);
  if (!context) {
    throw new Error(
      'usePaddleCheckout은 PaddleCheckoutProvider 안에서 사용해야 합니다.'
    );
  }
  return context;
};
